// engagement-components/AudienceBuilder.jsx
import React, { useState } from "react";
import { Card, Form, Button, Badge, Row, Col, InputGroup, Spinner, Alert } from "react-bootstrap";
import { FaFilter, FaUsers, FaTimes, FaUserEdit } from "react-icons/fa";
import dayjs from "dayjs";

// ─── Profile fields that can be "missing" ────────────────────────────────────
export const PROFILE_FIELDS = [
  { key: "anniversary", label: "Marriage anniversary",  icon: "💍", check: (g) => !g.anniversary },
  { key: "dob",         label: "Birthday / DOB",        icon: "🎂", check: (g) => !g.dob         },
  { key: "email",       label: "Email address",         icon: "✉️", check: (g) => !g.email        },
  { key: "phone",       label: "Phone number",          icon: "📞", check: (g) => !g.phone        },
  { key: "city",        label: "City / Location",       icon: "📍", check: (g) => !g.city         },
];

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const TAGS   = ["VIP","Regular","First Visit","Loyalty Member","Corporate","Honeymoon","Anniversary Stay","Birthday Stay"];

// ─── Default empty filter state ───────────────────────────────────────────────
export const emptyFilters = () => ({
  // Basic
  city:                "",
  status:              "Active",
  source:              "",
  tags:                [],
  roomType:            "",
  // Date-based
  birthdayMonth:       "",
  anniversaryDateFrom: "",
  anniversaryDateTo:   "",
  lastVisitDaysAgo:    "",
  hasCheckinUpcoming:  false,
  hasCheckoutUpcoming: false,
  // Profile completeness — NEW
  missingFields:       [],    // array of PROFILE_FIELDS keys
  onlyIncomplete:      false, // shortcut: any field missing
});

// ─── Client-side filter logic ─────────────────────────────────────────────────
export const applyFilters = (guests, filters) => {
  return guests.filter((g) => {
    // City
    if (filters.city && !g.city?.toLowerCase().includes(filters.city.toLowerCase())) return false;
    // Status
    if (filters.status && filters.status !== "All" && g.status !== filters.status) return false;
    // Source
    if (filters.source && g.source !== filters.source) return false;
    // Tags — guest must have ALL selected tags
    if (filters.tags.length > 0 && !filters.tags.every((t) => g.tags?.includes(t))) return false;
    // Room type
    if (filters.roomType && g.room_type !== filters.roomType) return false;
    // Birthday month
    if (filters.birthdayMonth) {
      if (!g.dob) return false;
      if (String(dayjs(g.dob).month() + 1) !== filters.birthdayMonth) return false;
    }
    // Anniversary date range (MM-DD, year-agnostic)
    if (filters.anniversaryDateFrom || filters.anniversaryDateTo) {
      if (!g.anniversary) return false;
      const mmdd = dayjs(g.anniversary).format("MM-DD");
      if (filters.anniversaryDateFrom && mmdd < filters.anniversaryDateFrom) return false;
      if (filters.anniversaryDateTo   && mmdd > filters.anniversaryDateTo)   return false;
    }
    // Last visit
    if (filters.lastVisitDaysAgo) {
      if (!g.lastInteraction) return false;
      if (dayjs(g.lastInteraction).isBefore(dayjs().subtract(Number(filters.lastVisitDaysAgo), "day"))) return false;
    }
    // Check-in upcoming
    if (filters.hasCheckinUpcoming) {
      const ci = g.checkin_date ? dayjs(g.checkin_date) : null;
      if (!ci || !(ci.isAfter(dayjs()) && ci.isBefore(dayjs().add(7, "day")))) return false;
    }
    // Check-out upcoming
    if (filters.hasCheckoutUpcoming) {
      const co = g.checkout_date ? dayjs(g.checkout_date) : null;
      if (!co || !(co.isAfter(dayjs()) && co.isBefore(dayjs().add(7, "day")))) return false;
    }

    // ── Profile completeness filters ──────────────────────────────────────
    // "Any incomplete" shortcut
    if (filters.onlyIncomplete) {
      const anyMissing = PROFILE_FIELDS.some((f) => f.check(g));
      if (!anyMissing) return false;
    }
    // Specific missing fields — guest must be missing ALL selected fields
    if (filters.missingFields.length > 0) {
      const fieldDefs  = PROFILE_FIELDS.filter((f) => filters.missingFields.includes(f.key));
      const allMissing = fieldDefs.every((f) => f.check(g));
      if (!allMissing) return false;
    }

    return true;
  });
};

// ─── Completeness score helper — used in campaign summary ────────────────────
export const profileCompleteness = (guest) => {
  const filled = PROFILE_FIELDS.filter((f) => !f.check(guest)).length;
  return Math.round((filled / PROFILE_FIELDS.length) * 100);
};

// ─── Filter pill component ────────────────────────────────────────────────────
const FilterPill = ({ label, onRemove }) => (
  <Badge className="d-inline-flex align-items-center gap-1 me-1 mb-1"
    style={{ background: "#e0e7ff", color: "#3730a3", fontWeight: 500, fontSize: "0.75rem", padding: "5px 10px" }}>
    {label}
    <button onClick={onRemove} className="border-0 bg-transparent p-0 ms-1"
      style={{ color: "#6366f1", cursor: "pointer", lineHeight: 1 }} aria-label={`Remove ${label} filter`}>
      ×
    </button>
  </Badge>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const AudienceBuilder = ({ filters, setFilters, matchedCount, totalCount, loading }) => {
  const [showAdvanced,  setShowAdvanced]  = useState(false);
  const [showProfile,   setShowProfile]   = useState(
    // auto-open profile section if any profile filter already set
    filters.onlyIncomplete || filters.missingFields.length > 0
  );

  const set = (field, val) => setFilters((p) => ({ ...p, [field]: val }));

  const toggleTag = (tag) =>
    setFilters((p) => ({
      ...p,
      tags: p.tags.includes(tag) ? p.tags.filter((t) => t !== tag) : [...p.tags, tag],
    }));

  const toggleMissingField = (key) =>
    setFilters((p) => ({
      ...p,
      missingFields: p.missingFields.includes(key)
        ? p.missingFields.filter((k) => k !== key)
        : [...p.missingFields, key],
      // If picking specific fields, turn off the "any" shortcut
      onlyIncomplete: false,
    }));

  const clearAll = () => setFilters(emptyFilters());

  // Build active pills for summary display
  const activePills = [];
  if (filters.city)                activePills.push({ label: `City: ${filters.city}`,                              key: "city", clear: () => set("city", "") });
  if (filters.roomType)            activePills.push({ label: `Room: ${filters.roomType}`,                          key: "rt",   clear: () => set("roomType", "") });
  if (filters.source)              activePills.push({ label: `Source: ${filters.source}`,                          key: "src",  clear: () => set("source", "") });
  if (filters.birthdayMonth)       activePills.push({ label: `Birthday: ${MONTHS[+filters.birthdayMonth-1]}`,      key: "bm",   clear: () => set("birthdayMonth", "") });
  if (filters.anniversaryDateFrom) activePills.push({ label: `Anniv. from ${filters.anniversaryDateFrom}`,         key: "af",   clear: () => set("anniversaryDateFrom", "") });
  if (filters.anniversaryDateTo)   activePills.push({ label: `Anniv. to ${filters.anniversaryDateTo}`,             key: "at",   clear: () => set("anniversaryDateTo", "") });
  if (filters.lastVisitDaysAgo)    activePills.push({ label: `Visited ≤ ${filters.lastVisitDaysAgo}d ago`,         key: "lv",   clear: () => set("lastVisitDaysAgo", "") });
  if (filters.hasCheckinUpcoming)  activePills.push({ label: "Check-in next 7d",                                    key: "ci",   clear: () => set("hasCheckinUpcoming", false) });
  if (filters.hasCheckoutUpcoming) activePills.push({ label: "Check-out next 7d",                                   key: "co",   clear: () => set("hasCheckoutUpcoming", false) });
  if (filters.onlyIncomplete)      activePills.push({ label: "Any profile field missing",                           key: "inc",  clear: () => set("onlyIncomplete", false) });
  filters.missingFields.forEach((k) => {
    const f = PROFILE_FIELDS.find((x) => x.key === k);
    if (f) activePills.push({ label: `Missing: ${f.label}`, key: `mf_${k}`, clear: () => toggleMissingField(k) });
  });
  filters.tags.forEach((t) => activePills.push({ label: `Tag: ${t}`, key: `tag_${t}`, clear: () => toggleTag(t) }));

  const hasFilters = activePills.length > 0;

  return (
    <Card className="shadow-sm border-0">
      <Card.Header className="bg-white border-bottom py-3">
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="fw-bold mb-0">
            <FaFilter className="text-primary me-2" />Audience
          </h6>
          <div className="d-flex align-items-center gap-3">
            {loading && <Spinner size="sm" animation="border" variant="primary" />}
            <span style={{ fontSize: "0.9rem" }}>
              <FaUsers className="me-1 text-primary" />
              <strong className="text-primary">{matchedCount}</strong>
              <span className="text-muted"> / {totalCount} guests</span>
            </span>
            {hasFilters && (
              <Button variant="link" size="sm" className="p-0 text-danger text-decoration-none" onClick={clearAll}>
                <FaTimes size={11} className="me-1" />Clear all
              </Button>
            )}
          </div>
        </div>
      </Card.Header>

      <Card.Body className="p-4">

        {/* ── Row 1: City + Room Type + Status + Source ─────────────────── */}
        <Row className="g-3 mb-3">
          <Col md={3}>
            <Form.Label className="fw-semibold small">City / Location</Form.Label>
            <Form.Control size="sm" value={filters.city}
              onChange={(e) => set("city", e.target.value)}
              placeholder="e.g. Mumbai, Delhi..." aria-label="Filter by city" />
          </Col>
          <Col md={3}>
            <Form.Label className="fw-semibold small">Room Type</Form.Label>
            <Form.Select size="sm" value={filters.roomType} onChange={(e) => set("roomType", e.target.value)}>
              <option value="">All room types</option>
              {["Standard","Deluxe","Suite","Villa","Presidential Suite","Cottage"].map((r) => (
                <option key={r}>{r}</option>
              ))}
            </Form.Select>
          </Col>
          <Col md={3}>
            <Form.Label className="fw-semibold small">Status</Form.Label>
            <Form.Select size="sm" value={filters.status} onChange={(e) => set("status", e.target.value)}>
              <option value="All">All statuses</option>
              <option value="Active">Active only</option>
              <option value="Inactive">Inactive</option>
            </Form.Select>
          </Col>
          <Col md={3}>
            <Form.Label className="fw-semibold small">Source</Form.Label>
            <Form.Select size="sm" value={filters.source} onChange={(e) => set("source", e.target.value)}>
              <option value="">All sources</option>
              {["PMS","CSV Import","Manual","Feedback Form","QR Registration"].map((s) => (
                <option key={s}>{s}</option>
              ))}
            </Form.Select>
          </Col>
        </Row>

        {/* ── Row 2: Tags ───────────────────────────────────────────────── */}
        <div className="mb-3">
          <Form.Label className="fw-semibold small">Tags / Segments</Form.Label>
          <div className="d-flex flex-wrap gap-2">
            {TAGS.map((tag) => (
              <Button key={tag} size="sm"
                variant={filters.tags.includes(tag) ? "primary" : "outline-secondary"}
                className="rounded-pill"
                onClick={() => toggleTag(tag)}
                aria-pressed={filters.tags.includes(tag)}>
                {tag}
              </Button>
            ))}
          </div>
        </div>

        {/* ── Profile Completeness Filter — KEY SECTION ─────────────────── */}
        <div className="mb-3">
          <Button variant="link" size="sm" className="p-0 text-decoration-none d-flex align-items-center gap-2"
            onClick={() => setShowProfile(!showProfile)}>
            <FaUserEdit className="text-warning" />
            <span className="fw-semibold text-dark">Profile completeness filter</span>
            <i className={`fas fa-chevron-${showProfile ? "up" : "down"} ms-1 text-muted`}
              style={{ fontSize: "0.72rem" }}></i>
            {(filters.onlyIncomplete || filters.missingFields.length > 0) && (
              <Badge bg="warning" text="dark" style={{ fontSize: "0.65rem" }}>Active</Badge>
            )}
          </Button>

          {showProfile && (
            <div className="mt-3 p-3 rounded border" style={{ background: "#fffbeb", borderColor: "#fde68a" }}>
              <div className="mb-3">
                <Form.Check
                  type="switch"
                  id="only-incomplete-switch"
                  label={
                    <span>
                      <strong>Any incomplete profile</strong>
                      <span className="text-muted ms-2 fw-normal" style={{ fontSize: "0.8rem" }}>
                        — include guests missing at least one field
                      </span>
                    </span>
                  }
                  checked={filters.onlyIncomplete}
                  onChange={(e) => setFilters((p) => ({
                    ...p,
                    onlyIncomplete: e.target.checked,
                    missingFields:  e.target.checked ? [] : p.missingFields,
                  }))}
                />
              </div>

              {!filters.onlyIncomplete && (
                <div>
                  <Form.Label className="fw-semibold small text-muted d-block mb-2">
                    Or filter by specific missing fields (guest must be missing ALL ticked fields):
                  </Form.Label>
                  <div className="d-flex flex-wrap gap-2">
                    {PROFILE_FIELDS.map((f) => (
                      <Button key={f.key} size="sm"
                        variant={filters.missingFields.includes(f.key) ? "warning" : "outline-secondary"}
                        className="rounded-pill"
                        onClick={() => toggleMissingField(f.key)}
                        aria-pressed={filters.missingFields.includes(f.key)}>
                        {f.icon} {f.label}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* Explain what the message will say */}
              {(filters.onlyIncomplete || filters.missingFields.length > 0) && (
                <div className="mt-3 p-2 rounded" style={{ background: "#fff7ed", border: "1px solid #fed7aa", fontSize: "0.78rem" }}>
                  <i className="fas fa-lightbulb text-warning me-2"></i>
                  <strong>Tip:</strong> Use a template with a <strong>self-registration link</strong> so guests can
                  complete their profile in one tap. Offer 10% off as incentive using the
                  <code className="ms-1">{"{{link}}"}</code> variable pointing to your profile completion page.
                  <br />
                  <span className="text-muted">
                    Sample message: "Hi {"{{guest_name}}"}, complete your profile and get 10% off your next stay → {"{{link}}"}  🎁"
                  </span>
                </div>
              )}

              <div className="mt-2 alert alert-secondary py-2 mb-0" style={{ fontSize: "0.78rem" }}>
                <i className="fas fa-info-circle me-2"></i>
                Use <strong>WhatsApp</strong> for guests who have a phone number,
                <strong> Email</strong> for those who don't — set this in the channel fallback below.
              </div>
            </div>
          )}
        </div>

        {/* ── Advanced filters toggle ───────────────────────────────────── */}
        <Button variant="link" size="sm" className="p-0 text-decoration-none mb-3"
          onClick={() => setShowAdvanced(!showAdvanced)}>
          <i className={`fas fa-chevron-${showAdvanced ? "up" : "down"} me-1`}></i>
          {showAdvanced ? "Hide" : "Show"} date & stay filters
        </Button>

        {showAdvanced && (
          <div className="p-3 rounded border mb-3" style={{ background: "#f8faff" }}>
            <Row className="g-3">
              <Col md={3}>
                <Form.Label className="fw-semibold small">🎂 Birthday month</Form.Label>
                <Form.Select size="sm" value={filters.birthdayMonth}
                  onChange={(e) => set("birthdayMonth", e.target.value)}>
                  <option value="">Any month</option>
                  {MONTHS.map((m, i) => <option key={m} value={String(i+1)}>{m}</option>)}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label className="fw-semibold small">💍 Anniversary from</Form.Label>
                <Form.Control type="date" size="sm"
                  value={filters.anniversaryDateFrom ? `2000-${filters.anniversaryDateFrom}` : ""}
                  onChange={(e) => set("anniversaryDateFrom", e.target.value ? e.target.value.slice(5) : "")} />
                <Form.Text className="text-muted" style={{ fontSize: "0.68rem" }}>Year ignored</Form.Text>
              </Col>
              <Col md={3}>
                <Form.Label className="fw-semibold small">💍 Anniversary to</Form.Label>
                <Form.Control type="date" size="sm"
                  value={filters.anniversaryDateTo ? `2000-${filters.anniversaryDateTo}` : ""}
                  onChange={(e) => set("anniversaryDateTo", e.target.value ? e.target.value.slice(5) : "")} />
              </Col>
              <Col md={3}>
                <Form.Label className="fw-semibold small">Last visit within</Form.Label>
                <InputGroup size="sm">
                  <Form.Control type="number" min="1" max="365"
                    value={filters.lastVisitDaysAgo}
                    onChange={(e) => set("lastVisitDaysAgo", e.target.value)}
                    placeholder="e.g. 90" />
                  <InputGroup.Text>days</InputGroup.Text>
                </InputGroup>
              </Col>
              <Col xs={12} className="d-flex gap-4">
                <Form.Check type="switch" id="ci-sw" label="Check-in next 7 days"
                  checked={filters.hasCheckinUpcoming}
                  onChange={(e) => set("hasCheckinUpcoming", e.target.checked)} />
                <Form.Check type="switch" id="co-sw" label="Check-out next 7 days"
                  checked={filters.hasCheckoutUpcoming}
                  onChange={(e) => set("hasCheckoutUpcoming", e.target.checked)} />
              </Col>
            </Row>
          </div>
        )}

        {/* ── Active pills summary ──────────────────────────────────────── */}
        {hasFilters && (
          <div>
            <small className="text-muted fw-semibold d-block mb-2">Active filters:</small>
            {activePills.map((p) => (
              <FilterPill key={p.key} label={p.label} onRemove={p.clear} />
            ))}
          </div>
        )}

        {/* Zero-match warning */}
        {!loading && hasFilters && matchedCount === 0 && (
          <Alert variant="warning" className="py-2 mt-3 mb-0" style={{ fontSize: "0.82rem" }}>
            <i className="fas fa-exclamation-triangle me-2"></i>
            No guests match these filters. Try broadening your criteria.
          </Alert>
        )}

        {!hasFilters && (
          <div className="text-muted small">
            <i className="fas fa-info-circle me-1"></i>
            No filters — all <strong>{totalCount}</strong> active guests will receive this campaign.
          </div>
        )}

      </Card.Body>
    </Card>
  );
};

export default AudienceBuilder;
